import React, {useMemo} from 'react';
import {
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {TagResponse} from '../../shared/tags';
import {useDeleteTagMutation, useGetTagDetails} from '../../hooks/api/tags';
import {pluralize} from '../../lib/helpers';
import {isTaggedAccountsFilter} from '../../shared/fields/enrichment';
import {useCustomer} from '../../hooks/api/metadata';
import {ObjectId} from 'bson';
import {useIsRescoring} from '../../hooks/api/scoringInfo';

const useCanDeleteTag = (tagId: ObjectId) => {
  const isRescoring = useIsRescoring();
  const customer = useCustomer();
  const agentsUsingTag = customer.agents?.filter((agent) => {
    if (isTaggedAccountsFilter(agent.refreshSettings?.accountFilter)) {
      return agent.refreshSettings.accountFilter.tags.some((tag) =>
        tag.equals(tagId)
      );
    }
    return false;
  });

  const disabledReason = useMemo(() => {
    if (agentsUsingTag?.length) {
      return `List in use by ${pluralize(
        agentsUsingTag.length,
        'agent',
        'agents'
      )}. Remove from agent filters before deleting.`;
    }
    if (isRescoring) {
      return 'Deleting lists is disabled while rescoring is in progress.';
    }
    return undefined;
  }, [agentsUsingTag, isRescoring]);

  return {
    isDeleteTagDisabled: !!agentsUsingTag?.length || isRescoring,
    disabledReason,
  };
};

export const DeleteTagModal = ({
  tag,
  isOpen,
  onClose,
}: {
  tag: TagResponse;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const {isDeleteTagDisabled, disabledReason} = useCanDeleteTag(tag._id);
  const {data: tagDetails} = useGetTagDetails({tagId: tag._id});
  const {mutate, isPending} = useDeleteTagMutation();
  const toast = useToast();

  const handleDeleteTag = () => {
    mutate(
      {tagId: tag._id},
      {
        onSuccess: () => {
          toast({
            title: 'List deleted',
            status: 'success',
            duration: 2_000,
            isClosable: true,
            position: 'bottom',
          });
          onClose();
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontWeight="normal" pb="1" pt="6">
          Delete &apos;{tag.name}&apos; list?
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px="6" py="6" color="kgray.300">
          {tagDetails?.numAccounts !== undefined
            ? pluralize(tagDetails.numAccounts, 'account', 'accounts')
            : 'All matching accounts'}{' '}
          will no longer be associated with this list.
        </ModalBody>
        <Divider />
        <ModalFooter justifyContent="start" gap="4">
          <Tooltip
            isDisabled={!disabledReason}
            label={disabledReason}
            placement="top"
          >
            <Button
              onClick={handleDeleteTag}
              colorScheme="kred"
              isLoading={isPending}
              isDisabled={isDeleteTagDisabled}
            >
              Delete
            </Button>
          </Tooltip>
          <Button color="kgray.300" onClick={onClose} variant="ghost">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
